import type { CancelToken } from 'axios';
import type { FilterTree } from '~/_shared/types/filterTree.types';
import type { MapId } from '~/_shared/types/map';
import type { SpreadsheetId } from '~/_shared/types/spreadsheetData/spreadsheetRow';
import { apiPost } from '~/_shared/utils/api/api.helpers';
import type {
  BoundaryGroupId, BoundaryId, BoundaryTerritoryGroupId,
} from '~/store/boundaries/boundaryIdentifier.type';
import type { MetricServerModel } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.repository';
import type { ValidateTerritoryConstraintResultItem } from '../createBoundaryGroupFromAIModal/modalSteps/constraintsValidation/constraints.repository';

export type CreateOptimizedTerritoriesResponse = {
  message: string;
  hash?: string;
};

export type OptimizedTerritoryAggMetric = {
  name: string;
  attribute: string; // Value should be data column_ or demographic parameter like US_STATES|POPULATION|TOTAL_POPULATION
  weight: number;
};

export type OptimizedTerritoryGlobalMetric = {
  name: string;
  weight: number;
};

export type OptimizedTerritoryConstraint = {
  attribute: string; // column or demographic
  lowerBound: number;
  upperBound: number;
};

export type OptimizedTerritoryConstraintServerModel = {
  attribute: string; // column or demographic
  lower_bound: number;
  upper_bound: number;
};

export type OptimizedTerritoryAnchorMap = {
  mapId: MapId;
  nameColumnId: string;
};

export type OptimizedTerritoryAnchorMapServerModel = {
  map_id: MapId;
  name_column: string;
};

export type OptimizedTerritoryBtgSettings = {
  metrics?: ReadonlyArray<MetricServerModel>;
};

export type CreateOptimizedTerritoriesRequest = {
  map_id: MapId;
  spreadsheet_id: SpreadsheetId;
  boundary_group_id: BoundaryGroupId;
  agg_metrics: ReadonlyArray<OptimizedTerritoryAggMetric>;
  global_metrics: ReadonlyArray<OptimizedTerritoryGlobalMetric>;
  iterations: number; // at least 2; [2, ...]
  display_name: string;

  target_territory_count?: number; // required if anchors_map IS NOT provided; [2, 1000]
  ap_importance?: number; // required if anchors_map IS provided; [0, 100]
  exclude_disconnected_blocks?: boolean;
  block_subset?: ReadonlyArray<BoundaryId>;
  constraints?: ReadonlyArray<OptimizedTerritoryConstraintServerModel>;
  anchors_map?: OptimizedTerritoryAnchorMapServerModel;
  previous_territories_boundary_group_id?: BoundaryTerritoryGroupId;
  btg_settings?: OptimizedTerritoryBtgSettings;
  export_config?: boolean;
  filter?: { filter_tree: FilterTree };
};

export const createOptimizedTerritories = (
  clientId: number, request: CreateOptimizedTerritoriesRequest
): Promise<CreateOptimizedTerritoriesResponse> => {
  const requestUrl = `/api/clients/${clientId}/maptive-optimized-territories/territories`;

  return apiPost(requestUrl, request);
};

export type ValidateMOTConstraintsItem = {
  attribute: string;
  lower_bound: number;
  upper_bound: number;
} | {
  attribute: 'location_count';
  column_id: string;
  column_value: string;
  lower_bound: number;
  upper_bound: number;
};

export type ValidateMOTConstraintsRequest = Readonly<{
  map_id: number;
  spreadsheet_id: number;
  boundary_group_id: number;
  constraints: ReadonlyArray<ValidateMOTConstraintsItem>;

  block_subset?: ReadonlyArray<BoundaryId>;
  filter?: { filter_tree: FilterTree} ;
}>;

type ValidateMOTConstraintResultItem = ValidateTerritoryConstraintResultItem & {
  attribute: string;
};

type ValidateMOTConstraintsResponse = {
  results: ValidateMOTConstraintResultItem[];
};

export const validateOptimizedTerritoryConstraints = (
  clientId: number, params: ValidateMOTConstraintsRequest, cancelToken?: CancelToken
): Promise<ValidateMOTConstraintsResponse> => {
  const requestUrl = `/api/clients/${clientId}/maptive-optimized-territories/constraints`;

  return apiPost(requestUrl, params, {
    cancelToken,
  });
};
